<template>
  <ParagraphLink
    v-for="paragraph in paragraphs"
    :key="paragraph.id"
    class="my-1 block rounded p-2 text-6pt transition-colors hover:bg-lexmea-gray-600"
    :legalText="legalTextSlug"
    :legalTextParagraph="paragraph.slug"
    :title="paragraph.title"
  >
    {{ paragraph.paragraph }} {{ paragraph.title }}
  </ParagraphLink>
  <Disclosure
    v-if="hasChildren"
    v-slot="{ open }"
    as="div"
    :defaultOpen="defaultOpen"
  >
    <DisclosureButton
      aria-label="disclosure"
      class="flex w-full items-center justify-start rounded bg-lexmea-gray-400 px-4 py-2 text-6pt font-bold transition hover:bg-lexmea-gray-600"
      :class="{
        'shadow-md': open,
      }"
    >
      <i-heroicons-chevron-right-20-solid
        :class="{ 'rotate-90': open }"
        class="mr-2 h-4 w-4 flex-shrink-0 transform transition"
      />
      <div class="flex flex-col items-start justify-center gap-2 text-start">
        <span class="flex-1" v-text="tocTitle"></span>
        <span
          v-if="includingParagraphs"
          class="font-normal"
          v-text="`${includingParagraphs}`"
        ></span>
      </div>
    </DisclosureButton>
    <DisclosurePanel as="ul" class="list-none">
      <li
        v-for="paragraph in (legalTextToc as IToC).legal_text_paragraphs"
        :key="paragraph.id"
        :ref="isElement(paragraph.id) ? 'paragraphElement' : ''"
        class="my-1 ml-8 rounded transition-colors hover:text-lexmea-pop-lightest"
        :class="{
          'bg-gray-200': isElement(paragraph.id),
        }"
      >
        <ParagraphLink
          class="block p-2 text-6pt"
          :legalText="legalTextSlug"
          :legalTextParagraph="paragraph.slug"
          :title="paragraph.title"
        >
          <div class="flex">
            <div class="flex-none">{{ paragraph.paragraph }}</div>
            <div class="pl-4">{{ paragraph.title }}</div>
          </div>
        </ParagraphLink>
      </li>
      <li
        v-for="child in (legalTextToc as IToC).children"
        :key="child.slug"
        class="my-2 ml-4 last:mb-6"
      >
        <LegalTextDisclosure
          :legalTextToc="child"
          :legalTextSlug="legalTextSlug"
          :tocOverviewParagraph="tocOverviewParagraph"
          :parentsOfActiveParagraph="parentsOfActiveParagraph"
        />
      </li>
    </DisclosurePanel>
  </Disclosure>
</template>

<script lang="ts" setup>
import type { ILegalTextParagraph } from "~/models/ILegalTextParagraph";
import type { IToC } from "~/models/IToC";

const props = defineProps<{
  legalTextToc: IToC | ILegalTextParagraph;
  legalTextSlug: string;
  tocOverviewParagraph?: ILegalTextParagraph;
  parentsOfActiveParagraph: IToC[];
}>();

function isParagraph(p: IToC | ILegalTextParagraph): p is ILegalTextParagraph {
  return (p as ILegalTextParagraph).paragraph !== undefined;
}

const tocEntryIsParagraph = computed(() => isParagraph(props.legalTextToc));

const hasChildren = computed(() => "children" in props.legalTextToc);

const tocTitle = isParagraph(props.legalTextToc)
  ? null
  : useTocTitle(props.legalTextToc);

const includingParagraphs = computed(() => {
  if (tocEntryIsParagraph.value) {
    return "";
  }

  const children = flattenToC(props.legalTextToc as IToC);

  // damn Eurlex and their formatting...
  // for Eurlex laws there are no paragraphs and no children
  if (children.length === 0) {
    return "";
  }

  if (children.length === 1) {
    return children[0].paragraph;
  }

  return [children[0].paragraph, children[children.length - 1]?.paragraph].join(
    " - "
  );
});

const flattenToC: (item: IToC) => ILegalTextParagraph[] = (item) => {
  const paragraphs = [...item.legal_text_paragraphs];
  if (item.children?.length) {
    paragraphs.push(...item.children.flatMap((child) => flattenToC(child)));
  }
  return paragraphs;
};

const paragraphElement = ref<HTMLLIElement>();

onMounted(() => {
  if (paragraphElement.value) {
    // select first element, because :ref was unsed inside v-for, so paragraphElement is an array
    const element = paragraphElement.value[0] as HTMLElement;
    element.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "start",
    });
  }
});
const isElement = (id: number) =>
  props.tocOverviewParagraph && id === props.tocOverviewParagraph.id;

const defaultOpen = computed(
  () =>
    !tocEntryIsParagraph.value &&
    props.parentsOfActiveParagraph.includes(props.legalTextToc as IToC)
);

const paragraphs = computed(() => {
  if (tocEntryIsParagraph.value) {
    return [props.legalTextToc as ILegalTextParagraph];
  }

  if (hasChildren.value) {
    return [];
  }

  return flattenToC(props.legalTextToc as IToC);
});
</script>
